import { useEffect, useState, useRef, useCallback } from 'react';
import { useDialog } from './DialogContext';
import { useLocation, useHistory } from 'react-router-dom';
import { SubscriptionRequest } from 'src/request/Subscription';
import $axios from './axios';

const ONE_MIN_MS = 60 * 1000;
const ONE_HOUR_MS = 60 * ONE_MIN_MS;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
const THREE_DAYS_MS = 3 * ONE_DAY_MS;

const useSubscriptionCheck = () => {
  const { openDialog, closeDialog } = useDialog();
  const location = useLocation();
  const [expiryDate, setExpiryDate] = useState(null);
  const intervalTimeRef = useRef(ONE_HOUR_MS);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const isLoggedIn = !!localStorage.getItem('token');
  const showDialogRef = useRef(true);
  const isMounted = useRef(true);
  const history = useHistory();
  const Swal = require('sweetalert2');
  const excludedRoutes = ['/', '/404', '/500', '/payment/success', '/payment/failed'];
  const isPublicPortal = location.pathname.startsWith('/gov-portal');
  const navigateToRequestSubscription = () => {
    history.push({ pathname: '/subscription/subscription-details' });
    closeDialog();
  };
  const addCommas = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  const fetchSubscriptionData = useCallback(async () => {
    try {
      const response = await $axios.get('pricing/get-subscription-expiry');

      const { data } = response;
      const { date_end, balance, status, credit_status } = data.data;
      const expiry = date_end ? new Date(date_end) : null;
      setExpiryDate(expiry);
      setIsSubscribed(!!status);

      return { expiry, balance, status, credit_status };
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      return { expiry: null, balance: null, status: null, credit_status: null };
    }
  }, []);

  const hideDialog = () => {
    showDialogRef.current = false;
  };
  const NoCreditsLeft = () => {
    return openDialog({
      title: 'No Credits Left!',
      message: 'You have used all your credit. Contact your Super Admin for additional credits.',
      onPrimaryAction: () => navigateToRequestSubscription(),
      primaryActionText: 'Contact Super Admin',
      iconType: 'warning',
      iconColor: '#F2AC57',
      onClose: () => hideDialog(),
    });
  };
  const handleSubscriptionRequest = async () => {
    const { balance } = await fetchSubscriptionData();
    if (balance < 250) {
      NotEnoughCreditForResubscription(balance);
      return;
    }
    return SubscriptionRequest()
      .then((res) => {
        if (res.status === 200) {
          Swal.fire({
            title: 'Subscription Successful',
            icon: 'success',
            text: 'You have successfully subscribed to Power Tools.',
          });
          return 1;
        }
        throw new Error('Unexpected response status');
      })
      .catch((error) => {
        console.log('Something went wrong with the subscription request:', error);
      })
      .finally(closeDialog);
  };
  const checkExpiry = useCallback(async () => {
    const { expiry, balance, status, credit_status } = await fetchSubscriptionData();
    const hasNoCredits = status === 0 && balance != null && +balance === 0;
    const shouldShowDialog = showDialogRef.current && !credit_status;
    if (hasNoCredits && shouldShowDialog) {
      NoCreditsLeft();
    }
    if (!expiry) return;

    const now = new Date();
    const timeRemaining = expiry - now;

    if (timeRemaining <= 0 && showDialogRef.current) {
      const hasEnoughBalance = balance >= 250;
      if (hasEnoughBalance || !credit_status) {
        openDialog({
          title: 'Subscription Expired!',
          message: hasEnoughBalance
            ? `Your subscription has expired but you still have P${addCommas(balance)} credits. Would you like to renew your subscription using your credits?`
            : 'Your subscription has expired, and you don’t have enough credits to renew. Would you like to contact your super admin for additional credits?.',
          onPrimaryAction: () => (hasEnoughBalance ? handleSubscriptionRequest() : navigateToRequestSubscription()),
          primaryActionText: hasEnoughBalance ? 'Subscribe for P250' : 'Contact Super Admin',
          iconType: 'warning',
          iconColor: '#F2AC57',
          onClose: () => hideDialog(),
        });
      }
      intervalTimeRef.current = ONE_MIN_MS;
      return;
    }

    if (timeRemaining <= THREE_DAYS_MS && status === 1 && showDialogRef.current) {
      openDialog({
        title: 'Subscription Expiring Soon!',
        message: 'Your subscription will expire soon. Would you like to resubscribe?',
        onPrimaryAction: () => handleSubscriptionRequest(),
        primaryActionText: 'Subscribe for P250',
        iconType: 'warning',
        iconColor: '#F2AC57',
        onClose: () => hideDialog(),
      });
      intervalTimeRef.current = ONE_MIN_MS;
    }
  }, [fetchSubscriptionData, openDialog, closeDialog]);

  const NotEnoughCreditForResubscription = (balance) => {
    openDialog({
      title: 'Insufficient Credit',
      message: `You currently have P${addCommas(balance)} credits, which is insufficient for the Power Tools subscription at P250/month. Please contact your Super Admin to request additional credits.`,
      onPrimaryAction: () => navigateToRequestSubscription(),
      primaryActionText: 'Contant Super Admin',
      iconType: 'warning',
      iconColor: '#F2AC57',
      onClose: () => hideDialog(),
    });
  };
  if (isMounted.current) {
    if (isLoggedIn && !isPublicPortal && !excludedRoutes.some((route) => location.pathname === route)) fetchSubscriptionData().then(() => checkExpiry());
    isMounted.current = false;
  }

  useEffect(() => {
    if (!expiryDate || !showDialogRef.current) return;

    if (isPublicPortal || excludedRoutes.some((route) => location.pathname === route)) {
      closeDialog();
      return;
    }

    const interval = setInterval(() => checkExpiry(), intervalTimeRef.current);

    return () => clearInterval(interval);
  }, [expiryDate, checkExpiry, location.pathname]);

  return { expiryDate, isSubscribed, refreshSubscription: fetchSubscriptionData, handleSubscriptionRequest, navigateToRequestSubscription, NoCreditsLeft };
};

export default useSubscriptionCheck;
